/**
 * client/src/features/income/components/BucketList/AddBucketButton.jsx
 * 
 * Modal component for adding a new bucket.
 * Provides a form for entering bucket details.
 */
import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import useBuckets from '../../hooks/useBuckets';

// Bucket types with icons
const BUCKET_TYPES = [
  { id: 'HOUSEHOLD', icon: '🏠', label: 'Household' },
  { id: 'BUSINESS', icon: '💼', label: 'Business' },
  { id: 'INVESTMENT', icon: '📈', label: 'Investment' },
  { id: 'SIDE_HUSTLE', icon: '💪', label: 'Side Hustle' },
  { id: 'CUSTOM', icon: '✨', label: 'Custom' }
];

/**
 * AddBucketButton Component
 * 
 * Button and modal for adding a new income bucket.
 * Can be used either as a controlled component with isOpen/onClose,
 * or as a self-contained component that manages its own state.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open (optional)
 * @param {Function} props.onClose - Function to close the modal (optional)
 * @param {Function} props.onSuccess - Function to call after successful bucket creation
 * @param {string} props.label - Custom label for the button (optional)
 */
const AddBucketButton = ({ isOpen: controlledIsOpen, onClose: controlledOnClose, onSuccess, label }) => {
  // Use internal state when the component is not controlled externally
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  
  // Determine if we're using controlled or uncontrolled mode
  const isControlled = controlledIsOpen !== undefined && controlledOnClose !== undefined;
  
  // Use controlled values if provided, otherwise use internal state
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const onClose = isControlled ? controlledOnClose : () => setInternalIsOpen(false);
  
  // Form state
  const [bucketName, setBucketName] = useState('');
  const [bucketType, setBucketType] = useState('HOUSEHOLD');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  
  // Get the createBucket function from the custom hook
  const { createBucket, refreshBuckets } = useBuckets();
  
  // Handle opening the modal
  const handleOpenModal = () => {
    if (!isControlled) {
      setInternalIsOpen(true);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bucketName.trim()) {
      setError('Bucket name is required');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      setDebugInfo('Creating bucket...');
      
      // Create the new bucket
      const newBucket = await createBucket({
        name: bucketName.trim(),
        type: bucketType,
        isExpanded: true, // New buckets start expanded
      });
      
      console.log('✅ Bucket created successfully:', newBucket);
      setDebugInfo(`Bucket created successfully: ${newBucket._id}`);
      
      // Reset form
      setBucketName('');
      setBucketType('HOUSEHOLD');
      
      // Close modal
      onClose();
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(newBucket);
      }
      
    } catch (err) {
      console.error('❌ Error creating bucket:', err);
      setError(err.message || 'Failed to create bucket. Please try again.');
      setDebugInfo(`Error: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Function to test localStorage access
  const testLocalStorage = () => {
    try {
      const testKey = 'theWallet_test';
      localStorage.setItem(testKey, 'test');
      const value = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      
      return `LocalStorage test successful: ${value === 'test' ? 'passed' : 'failed'}`;
    } catch (err) {
      return `LocalStorage test failed: ${err.message}`;
    }
  };
  
  // Render the button if we're in uncontrolled mode
  if (!isControlled && !internalIsOpen) {
    return (
      <button
        onClick={handleOpenModal}
        className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg flex items-center justify-center gap-1 px-4 py-2 transition-colors"
      >
        <PlusIcon className="w-5 h-5" />
        {label || 'Add Bucket'}
      </button>
    );
  }
  
  return (
    <>
      {/* Render button in uncontrolled mode if modal is not open */}
      {!isControlled && !internalIsOpen && (
        <button
          onClick={handleOpenModal}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg flex items-center justify-center gap-1 px-4 py-2 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          {label || 'Add Bucket'}
        </button>
      )}
      
      {/* Modal dialog */}
      <Dialog
        open={isOpen}
        onClose={onClose}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
          
          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto p-6">
            {/* Close button - Updated with purple styling */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 bg-indigo-600 text-white rounded-full p-1 hover:bg-indigo-700 transition-colors"
              aria-label="Close dialog"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            
            <Dialog.Title className="text-lg font-semibold text-gray-900 mb-4">
              Create New Income Bucket
            </Dialog.Title>
            
            <form onSubmit={handleSubmit}>
              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              {/* Debug info */}
              {debugInfo && (
                <div className="mb-4 p-3 bg-blue-50 text-blue-600 rounded-lg text-sm">
                  {debugInfo}
                  <div className="mt-1 text-xs">
                    {testLocalStorage()}
                  </div>
                </div>
              )}
              
              {/* Bucket name input - Updated to have a light background with dark text */}
              <div className="mb-4">
                <label htmlFor="bucketName" className="block text-sm font-medium text-gray-700 mb-1">
                  Bucket Name
                </label>
                <input
                  id="bucketName"
                  type="text"
                  value={bucketName}
                  onChange={(e) => setBucketName(e.target.value)}
                  placeholder="e.g., Primary Income, Side Projects"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              
              {/* Bucket type selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bucket Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {BUCKET_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setBucketType(type.id)}
                      className={`flex items-center p-3 rounded-lg ${
                        bucketType === type.id
                          ? 'bg-indigo-600 text-white'
                          : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                      }`}
                    >
                      <span className="text-xl mr-2">{type.icon}</span>
                      <span className="text-sm font-medium">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Form actions */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !bucketName.trim()}
                  className={`px-4 py-2 text-white rounded-lg transition-colors ${
                    isSubmitting || !bucketName.trim()
                      ? 'bg-indigo-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  {isSubmitting ? 'Creating...' : 'Create Bucket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddBucketButton; 